import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertCircle, Crown, Loader2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CreateFromLocationDialog({ loc, onClose, onDone }) {
  const [name, setName] = useState(loc?.name || "");
  const [cap, setCap] = useState(5);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setName(loc?.name || ""); setCap(5); }, [loc]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/tenants/from-location`, {
        location_id: loc.id, name: name.trim(), max_locations: Number(cap) || 1,
      });
      toast.success(`Tenant '${name.trim()}' created for ${loc.name}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create tenant");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!loc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-tenant-from-location-dialog">
        <DialogHeader>
          <DialogTitle>Create tenant for {loc?.name}</DialogTitle>
          <DialogDescription>
            The location and all its users move under this new tenant. Its current admin becomes the tenant owner.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tenant name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="from-location-name-input" />
          </div>
          <div>
            <Label>Location cap (how many locations this tenant can hold)</Label>
            <Input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} data-testid="from-location-cap-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="from-location-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !name.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="from-location-submit-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create tenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnassignedRow({ loc, tenants, onCreate, onChanged }) {
  const [moving, setMoving] = useState(false);
  const move = async (tenantId) => {
    setMoving(true);
    try {
      const res = await axios.post(`${API}/admin/tenants/${tenantId}/assign-location`, null, { params: { location_id: loc.id } });
      toast.success(res.data.message || "Location moved");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to move location");
    } finally {
      setMoving(false);
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b last:border-b-0" data-testid={`unassigned-location-row-${loc.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <MapPin className="h-4 w-4 text-amber-600 shrink-0" />
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{loc.name}</div>
          <div className="text-xs text-gray-500">
            {[loc.city, loc.state].filter(Boolean).join(", ") || "—"} · {loc.user_count} user{loc.user_count === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => onCreate(loc)} data-testid={`create-tenant-for-${loc.id}`}>
          <Crown className="h-3.5 w-3.5 mr-1.5" /> Create tenant
        </Button>
        <Select onValueChange={move} disabled={moving || tenants.length === 0}>
          <SelectTrigger className="w-48 h-9" data-testid={`move-to-tenant-${loc.id}`}>
            <SelectValue placeholder={moving ? "Moving…" : "Move into tenant…"} />
          </SelectTrigger>
          <SelectContent>
            {tenants.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`move-option-${loc.id}-${t.id}`}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export const UnassignedLocations = ({ tenants, onChanged, reloadKey }) => {
  const [locs, setLocs] = useState([]);
  const [createFor, setCreateFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/tenants/unassigned-locations`);
      setLocs(res.data.locations || []);
    } catch (err) {
      console.error("Failed to load unassigned locations", err);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const changed = () => { setCreateFor(null); load(); onChanged(); };

  if (locs.length === 0) return null;
  return (
    <Card className="mb-6 border-amber-300" data-testid="unassigned-locations-card">
      <CardContent className="p-0">
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900" data-testid="unassigned-locations-count">
              {locs.length} location{locs.length === 1 ? "" : "s"} without a tenant
            </div>
            <div className="text-xs text-amber-800">
              These were set up before tenants existed. Create a tenant for each, or move it into an existing one.
            </div>
          </div>
        </div>
        {locs.map((l) => (
          <UnassignedRow key={l.id} loc={l} tenants={tenants} onCreate={setCreateFor} onChanged={changed} />
        ))}
      </CardContent>
      <CreateFromLocationDialog loc={createFor} onClose={() => setCreateFor(null)} onDone={changed} />
    </Card>
  );
};
